import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertController, IonContent, LoadingController, ModalController, NavParams } from '@ionic/angular';
import moment from 'moment';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AgreementFormStep3Component } from 'src/app/pages/agreements/components/agreement-form/steps/agreement-form-step3/agreement-form-step3.component';
import { AGREEMENT_EVENT_NAMES } from 'src/app/pages/agreements/constants/agreement-event-data.constants';
import { AgreementsService } from 'src/app/pages/agreements/services/agreements.service';
import { BasePage } from 'src/app/pages/base.page';
import { UserExperienceFeedbackModalComponent } from 'src/app/shared/components/user-experience-feedback-modal/user-experience-feedback-modal.component';
import { Agreement, DaysAndTimes } from 'src/app/shared/models/gql/agreement.model';
import { Consumer } from 'src/app/shared/models/gql/consumer.model';
import { ModalDialogService } from 'src/app/shared/services/modal-dialog.service';
import { UserFeedbackService } from 'src/app/shared/services/user.feedback.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { getGraphQLErrorText } from 'src/app/shared/utilities/graphql-error-handling';

/**
 * @deprecated - please ensure all changes are made in the corresponding components and/or
 * CreateUpdateAgreementServicesComponent instead.
 */

@Component({
  selector: 'app-agreement-form-step2',
  templateUrl: './agreement-form-step2.component.html',
  styleUrls: ['./agreement-form-step2.component.scss'],
})
export class AgreementFormStep2Component extends BasePage implements OnInit {
  /** Current agreement step */
  readonly currentStep = 2;

  private statesForNew: Array<any> = ['requested', '', null, undefined];

  /** Class members */
  public agreement: Agreement = null;

  public consumer: Consumer;

  public form: UntypedFormGroup;

  @ViewChild(IonContent, { static: false }) content: IonContent;

  // radio group options for agreed on date and times control
  options = [
    { id: true, name: 'Yes' },
    {
      id: false,
      name: "No, we'll discuss and update the agreement later",
    },
  ];

  // Expected weekdays array
  public weekDaysOptions = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // time picker with 30 min difference
  public minutes: Array<number> = [0, 30];

  // current year for ion-dateTime selector
  currentYear: number;

  // Stores agreement status. True if it is a new agreement and false if it isn't.
  public isNewAgreement: boolean;

  /** Whether the form has been submitted */
  hasSubmitted = false;

  /** Whether the user is creating an agreement, amending an agreement or updating an offer  */
  action: 'createAgreement' | 'amendAgreement' | 'updateOffer' = 'createAgreement';

  /**
   * Whether the days and times section should be shown or not - this is hidden if the agreement was created from the
   * streamline flow whereby the days and times are deprecated in favour of planned sessions.
   */
  showDaysAndTimes = true;

  constructor(
    private navParams: NavParams,
    protected modalCtrl: ModalController,
    protected alertCtrl: AlertController, // inherited from BasePage, needs to be protected
    private agreementsService: AgreementsService,
    protected fb: UntypedFormBuilder,
    public loadingCtrl: LoadingController,
    public utilityService: UtilityService,
    private userFeedbackService: UserFeedbackService,
    private modalDialogService: ModalDialogService,
    private analyticsService: AnalyticsService,
  ) {
    super('AgreementFormStep2Component');
    const currentAgreement = this.navParams.get('agreement');
    this.agreement = this.amendInfoFor(currentAgreement);
    this.consumer = currentAgreement?.otherUser;
    this.isNewAgreement = this.statesForNew.includes(this.agreement.status);
  }

  ngOnInit() {
    this.setAction();

    // Setup form fields
    this.setupFormFields();
    this.currentYear = new Date().getFullYear();
    // initialize form and set values if agreement have daysAndTimes object
    if (this.agreement?.daysAndTimes) {
      this.initializeFormAndSetValues();
    }
  }

  /**
   * Set the service description on the form to the changes detected from the agreement support service form.
   *
   * @param serviceDescription - the new service description.
   */
  onServiceDescriptionChange(serviceDescription: string): void {
    this.form.controls.agreedService.setValue(serviceDescription);
  }

  /**
   * Set up the initial form fields
   */
  private async setupFormFields(): Promise<void> {
    let jobOccurrence = '';
    if (this.agreement?.jobOccurrence) {
      jobOccurrence = this.agreement?.jobOccurrence;
    } else if (this.agreement?.job) {
      jobOccurrence = 'weekly';
    }

    this.form = this.fb.group({
      agreedService: [this.agreement?.agreedService, Validators.required],
      jobFrequency: [
        this.agreement?.jobFrequency ? this.agreement?.jobFrequency : this.agreement?.job?.frequency || 'once_off',
        Validators.required,
      ],
      agreedOnSchedule: [this.agreement?.agreedOnSchedule || true, Validators.required],
      expectedEndDate: [this.agreement?.expectedEndDate],
      expectedStartDate: [this.agreement?.expectedStartDate || new Date().toISOString()],
      jobOccurrence: [jobOccurrence],
      expectedStartTime: [
        this.agreement?.expectedStartDate
          ? this.agreement?.expectedStartDate
          : new Date(new Date().setHours(24, 0)).toISOString(),
      ],
      scheduleDescription: [this.agreement?.scheduleDescription || ''],
      daysAndTimes: this.daysAndTimesFormGroup(),
    });
  }

  /**
   * Set the action for the agreement.
   */
  private setAction(): void {
    if (this.agreement.isAccepted && this.agreement.id) {
      this.action = 'amendAgreement';
    } else if (!this.agreement?.id) {
      this.action = 'createAgreement';
    } else {
      this.action = 'updateOffer';
    }

    // do not show the days and times section when amending the agreement if the agreement was created from
    // the streamline flow, i.e. daysAndTimes is null and agreedOnSchedule is false
    if (this.action !== 'createAgreement' && !this.agreement.agreedOnSchedule && !this.agreement.daysAndTimes) {
      this.showDaysAndTimes = false;
    }
  }

  /**
   * initializing form with week days
   */
  private daysAndTimesFormGroup(): UntypedFormGroup {
    return this.fb.group({
      monday: this.expectedDaysandTimes(),
      tuesday: this.expectedDaysandTimes(),
      wednesday: this.expectedDaysandTimes(),
      thursday: this.expectedDaysandTimes(),
      friday: this.expectedDaysandTimes(),
      saturday: this.expectedDaysandTimes(),
      sunday: this.expectedDaysandTimes(),
    });
  }

  /**
   * initializing form for particular day
   */
  private expectedDaysandTimes(): UntypedFormGroup {
    return this.fb.group({
      expected: [false],
      expectedSessions: this.fb.array([]),
    });
  }

  /**
   * Create the time slots based on the expected session for the weekdays.
   * If there are no hours available then create a single timeslot.
   */
  initializeFormAndSetValues(): void {
    this.weekDaysOptions.forEach((expectedItem) => {
      // creating the timeslots as many as the available hours
      this.form.controls.daysAndTimes
        .get(expectedItem)
        .get('expected')
        .setValue(this.agreement.daysAndTimes[expectedItem].expected);
      this.agreement.daysAndTimes[expectedItem].expectedSessions?.forEach((data) => {
        data.startAt = new Date(`${new Date().toDateString()} ${data.startAt}`).toISOString();
        data.endAt = new Date(`${new Date().toDateString()} ${data.endAt}`).toISOString();
        this.createExpectedTimeSlotForTheDay(expectedItem, data);
      });
      // There might be days that are enabled, but without any time slots. In that case, want to call this method
      // so that we can add an empty time slot.
      this.initOrClearTimeSlotsForDay(expectedItem, this.agreement.daysAndTimes[expectedItem].expected);
    });
  }

  /**
   * Submit agreement to the agreements service for update
   */
  async submit(): Promise<void> {
    this.hasSubmitted = true;
    this.form.markAllAsTouched();
    if (this.form.valid && this.validateHours()) {
      this.modalDialogService
        .openDialog({
          component: AgreementFormStep3Component,
          componentProps: { agreement: this.agreement },
          cssClass: 'fullpage-modal',
          id: 'agreement-step-3',
        })
        .subscribe((response) => {
          if (response?.data) {
            this.assignFormData(response);
          }
        });
    }
  }

  /**
   * Assign form data into agreement and submit the data.
   *
   * @param response - data from step 3 and step 4 of agreement forms.
   */
  private async assignFormData(response: any): Promise<void> {
    // suppose if we have expectedStartTime value then set that time value with expectedStartDate value.
    if (this.form.value.expectedStartTime) {
      const expectedStartDate = new Date(this.form.value.expectedStartDate);
      const expectedStartTime = new Date(this.form.value.expectedStartTime);
      expectedStartDate.setHours(
        expectedStartTime.getHours(),
        expectedStartTime.getMinutes(),
        expectedStartTime.getSeconds(),
      );
      this.form.get('expectedStartDate').setValue(expectedStartDate.toISOString());
    }
    // collect rate value and set in the agreement object
    this.agreement.agreementRates = response.data?.rates.map((item) => {
      const newItem = { ...item };
      delete newItem.priceLimitAlertType;
      delete newItem.tempRateId;
      delete newItem.workerType;
      if (newItem.budgetSupportTypes) {
        newItem.budgetSupportTypes = newItem.budgetSupportTypes.map((supportType) => supportType.id);
      }
      return newItem;
    });
    this.agreement.travelExpenseFlag = response.data?.is_travel_expense;
    this.agreement.perKmRateDollars = response.data?.per_km_travel_rate;
    this.agreement.supportCancel = response.data?.supportCancel;
    this.agreement.supportChange = response.data?.supportChange;
    this.agreement.supportFeedback = response.data?.supportFeedback;
    this.agreement.supportMinHours = response.data?.supportMinHours;

    this.showLoading();
    this.sendAgreement(response);
  }

  /**
   * Send the agreement.
   *
   * @param response - data from step 3 and step 4 of agreement forms.
   */
  private async sendAgreement(response: any): Promise<void> {
    let postData;
    if (this.action === 'amendAgreement') {
      postData = this.updateAgreementData(this.formatAgreement());
    } else if (this.action === 'createAgreement') {
      postData = this.newAgreementData(this.formatAgreement());
    } else {
      postData = this.updateAgreementData(this.formatAgreement());
    }
    try {
      // jobOccurrence accepts only "weekly" or "fortnightly" from backend so if that field is blank than we have to delete before send it to backend
      if (postData.attributes.jobOccurrence === '') {
        delete postData.attributes.jobOccurrence;
      }
      const res = await this.agreementsService[this.action](postData);
      await this.hideLoading();
      if (res) {
        this.agreementsService.refresh();
        this.okAlert(this.saveOkMessage()).then((alertRef: HTMLIonAlertElement) => {
          alertRef.present();
        });
        // agreement sent if action is 'createAgreement'
        if (this.action === 'createAgreement') {
          this.analyticsService.track(AGREEMENT_EVENT_NAMES.agreementSent, {
            ...this.agreementsService.analyticsClientProperties(this.agreement.otherUser.uuid, res.job?.uuid, res.uuid),
          });
        }
        // after alert instead of closing the modal, it should send back the data to the step 1.
        // Step1 will send back to view as it is expecting.
        this.modalCtrl.dismiss(this.agreement);
      }
    } catch (error) {
      let errorMsg = '';
      if (error.graphQLErrors) {
        errorMsg = getGraphQLErrorText(error.graphQLErrors);
      } else {
        errorMsg = error.message;
      }
      await this.hideLoading();
      await this.showErrorAlert(`Unable to process. ${errorMsg}`);
    }
  }

  /**
   * Update local copy w/ input from form
   */
  private formatAgreement(): Agreement {
    this.agreement.agreedService = this.form.value.agreedService;
    this.agreement.agreedOnSchedule = this.form.value.agreedOnSchedule;
    this.agreement.expectedStartDate = this.form.value.expectedStartDate;
    this.agreement.expectedEndDate = this.form.value.expectedEndDate;
    this.agreement.jobOccurrence = this.form.value.jobOccurrence;
    this.agreement.jobFrequency = this.form.value.jobFrequency;
    this.agreement.daysAndTimes = this.form.value.daysAndTimes;
    this.agreement.scheduleDescription = this.form.value.scheduleDescription;

    return this.agreement;
  }

  /**
   * Private utility members
   *
   * @param agreement
   */
  private amendInfoFor(agreement: Agreement): Agreement {
    if (agreement.agreementAmendment) {
      const amendment = agreement.agreementAmendment;

      agreement.agreedService = amendment.agreedService;
      agreement.agreementRates = amendment.agreementRates;
      agreement.daysAndTimes = amendment.daysAndTimes;
      agreement.jobFrequency = amendment.jobFrequency;
      agreement.jobOccurrence = amendment.jobOccurrence;
      agreement.expectedStartDate = amendment.expectedStartDate;
      agreement.expectedEndDate = amendment.expectedEndDate;
      agreement.scheduleDescription = amendment.scheduleDescription;
      agreement.travelExpenseFlag = amendment.travelExpenseFlag;
      agreement.perKmRateInDollars = amendment.perKmRateInDollars;
    }

    return agreement;
  }

  /**
   * Message for the alert when agreement was created successfully
   */
  private saveOkMessage(): string {
    const acceptedMessage = `${this.consumer.displayName} has been notified of proposed changes. Agreement will be updated once ${this.consumer.displayName} accepts these changes.`;
    const defaultMessage = 'Agreement was sent successfully.';

    return this.agreement.isAccepted ? acceptedMessage : defaultMessage;
  }

  /**
   * Returns the request payload for agreement creation
   *
   * @param data - Agreement object
   */
  private newAgreementData(data: Agreement): any {
    return {
      attributes: {
        consumerUserId: data.otherUser.id,
        postedJobId: data.job ? data.job.id : null,
        ratesInDollars: data.agreementRates,
        agreedService: data.agreedService,
        jobOccurrence: data.jobOccurrence,
        jobFrequency: data.jobFrequency,
        agreedOnSchedule: data.agreedOnSchedule,
        expectedEndDate: data.expectedEndDate,
        expectedStartDate: data.expectedStartDate,
        daysAndTimes: this.convertTo12HourFormat(data.daysAndTimes),
        scheduleDescription: data.scheduleDescription,
        travelExpenseFlag: data.travelExpenseFlag,
        perKmRateDollars: data.perKmRateDollars,
        supportCancel: data.supportCancel,
        supportChange: data.supportChange,
        supportFeedback: data.supportFeedback,
        supportMinHours: data.supportMinHours,
      },
    };
  }

  /**
   * Returns the request payload for agreement update. If the agreement was created from the streamline flow
   * do not send any scheduling information.
   *
   * @param agreement - Agreement object
   */
  private updateAgreementData(agreement: Agreement): any {
    if (!this.showDaysAndTimes) {
      return {
        attributes: {
          id: agreement.id,
          postedJobId: agreement.job ? agreement.job.id : null,
          ratesInDollars: agreement.agreementRates,
          agreedService: agreement.agreedService,
          travelExpenseFlag: agreement.travelExpenseFlag,
          perKmRateDollars: agreement.perKmRateDollars,
          supportCancel: agreement.supportCancel,
          supportChange: agreement.supportChange,
          supportFeedback: agreement.supportFeedback,
          supportMinHours: agreement.supportMinHours,
        },
      };
    }
    return {
      attributes: {
        id: agreement.id,
        postedJobId: agreement.job ? agreement.job.id : null,
        ratesInDollars: agreement.agreementRates,
        agreedService: agreement.agreedService,
        jobOccurrence: agreement.jobOccurrence,
        jobFrequency: agreement.jobFrequency,
        agreedOnSchedule: agreement.agreedOnSchedule,
        expectedEndDate: agreement.expectedEndDate,
        expectedStartDate: agreement.expectedStartDate,
        daysAndTimes: this.convertTo12HourFormat(agreement.daysAndTimes),
        scheduleDescription: agreement.scheduleDescription,
        travelExpenseFlag: agreement.travelExpenseFlag,
        perKmRateDollars: agreement.perKmRateDollars,
        supportCancel: agreement.supportCancel,
        supportChange: agreement.supportChange,
        supportFeedback: agreement.supportFeedback,
        supportMinHours: agreement.supportMinHours,
      },
    };
  }

  /**
   * Called by the toggle for each individual day.
   * Clears the hours for the day when the toggle is off.
   * Adds an empty set of startAt/endAt times for the day when the toggle is on.
   *
   * @param day - Name of the day that we are toggling on/off for.
   * @param isEnabled - The value of the toggle.
   */
  initOrClearTimeSlotsForDay(day: string, isEnabled: boolean) {
    // if toggle is off then clear the specific hour array
    // if toggle is on then add default single hour array
    const expectedSessionControl = this.form.controls.daysAndTimes
      .get(`${day}`)
      .get('expectedSessions') as UntypedFormArray;
    const expectedSessionHoursForSpecificDay = expectedSessionControl?.value;
    if (isEnabled) {
      if (expectedSessionHoursForSpecificDay?.length === 0) {
        expectedSessionControl.push(this.expectedSessionFormGroup());
      }
    } else if (expectedSessionHoursForSpecificDay?.length !== 0) {
      expectedSessionControl.clear();
    }
  }

  /**
   * Dynamically add time session form group.
   *
   * @param hoursData
   */
  private expectedSessionFormGroup(hoursData?): UntypedFormGroup {
    return this.fb.group({
      startAt: [hoursData?.startAt || '', [Validators.required]],
      endAt: [hoursData?.endAt || '', [Validators.required]],
    });
  }

  /**
   * Convert timestamp to 12 hour format.
   *
   * @param daysAndTimesData - daysAndTimes object
   */
  private convertTo12HourFormat(daysAndTimesData: DaysAndTimes): DaysAndTimes {
    this.weekDaysOptions.forEach((day) => {
      const data = daysAndTimesData[day];
      if (data.expected) {
        data.expectedSessions.forEach((timeData) => {
          timeData.startAt = moment(new Date(timeData.startAt).toString().match(/\d{2}:\d{2}/)[0], 'hh:mm a').format(
            'hh:mm a',
          );
          timeData.endAt = moment(new Date(timeData.endAt).toString().match(/\d{2}:\d{2}/)[0], 'hh:mm a').format(
            'hh:mm a',
          );
        });
      }
    });
    return daysAndTimesData;
  }

  /**
   * Removing expected time slot for the particular day.
   *
   * @param removeHoursForParticularDay - Remove the hours slot(start time and end time) from the form.
   * @param index - To remove the hours based on the index.
   */
  async removeExpectedHoursSlotForTheDay(removeHoursForParticularDay: string, index: number): Promise<void> {
    const expectedSessionControl = this.form.controls.daysAndTimes
      .get(removeHoursForParticularDay)
      .get('expectedSessions') as UntypedFormArray;
    // removing the timeslot for the specific weekday
    expectedSessionControl.removeAt(index);
  }

  /**
   * Create expectedSession hour slot for the particular day in the form.
   *
   * @param createHoursForParticularDay - Adding the hours slot(start time and end time) into the form for the particular day.
   * @param hourDataForParticularDay
   */
  createExpectedTimeSlotForTheDay(createHoursForParticularDay: string, hourDataForParticularDay): void {
    const expectedSessionControl = this.form.controls.daysAndTimes
      .get(createHoursForParticularDay)
      .get('expectedSessions') as UntypedFormArray;
    expectedSessionControl.push(this.expectedSessionFormGroup(hourDataForParticularDay));
  }

  /**
   * Validating the hours.
   * 1. Check that end time is after start time.
   * 2. Sorting the time in ascending order.
   * 3. Checking that whether its overlapping time slots.
   */
  private validateHours(): boolean {
    let validateTime = true;
    this.weekDaysOptions.forEach((dayItem) => {
      const expectedSessionControl = this.form.controls.daysAndTimes
        .get(dayItem)
        .get('expectedSessions') as UntypedFormArray;
      // convert the availibility hours into the 12 hour format
      const timings = JSON.parse(JSON.stringify(expectedSessionControl?.value));
      timings.forEach((timeData) => {
        timeData.startAt = moment(new Date(timeData.startAt).toString().match(/\d{2}:\d{2}/)[0], 'HH:mm').format(
          'HH:mm',
        );
        timeData.endAt = moment(new Date(timeData.endAt).toString().match(/\d{2}:\d{2}/)[0], 'HH:mm').format('HH:mm');
      });
      const timingsRow = timings;
      // sort the hours in the ascending order of the starttime
      timingsRow.sort(this.compareTimeSlotsArrays);

      // checking for the validation, endtime should be after the starttime
      timings.forEach((element, index) => {
        if (element?.endAt <= element?.startAt) {
          expectedSessionControl?.controls[index].setErrors({ isNotValidTimeSlot: true });
          validateTime = false;
        }
      });
      // checking for the overlap hours, no hours should overlap
      if (expectedSessionControl?.valid && timingsRow?.length > 1) {
        timingsRow.forEach((element, index) => {
          if (timingsRow[index + 1]) {
            const nextStartTime = timingsRow[index + 1]?.startAt;
            if (element?.endAt >= nextStartTime) {
              expectedSessionControl.setErrors({ isTimeSlotsOverlapping: true });
              validateTime = false;
            }
          }
        });
      }
    });
    return validateTime;
  }

  /**
   * Compare array of time slots. As this is used by arraySort in validateHours().
   *
   * @param a - Compare first row of start and end time.
   * @param b - Compare second row of start and end time.
   */
  private compareTimeSlotsArrays(a, b): number {
    if (a.endAt < b.endAt || (a.endAt === b.endAt && a.startAt > b.startAt)) {
      return -1;
    }
    if (a.endAt > b.endAt || (a.endAt === b.endAt && a.startAt < b.startAt)) {
      return 1;
    }
    return 0;
  }

  /**
   * add/remove validation on scheduleDescription field when job occurrence value change
   */
  toggleValidationScheduleDescription(): void {
    if (
      this.form.get('agreedOnSchedule').value &&
      this.form.get('jobFrequency').value === 'ongoing' &&
      this.form.get('jobOccurrence').value !== 'weekly'
    ) {
      this.form.get('scheduleDescription').setValidators(Validators.required);
      this.form.get('scheduleDescription').updateValueAndValidity();
      this.resetDaytime();
    } else {
      this.form.get('scheduleDescription').reset();
      this.form.get('scheduleDescription').clearValidators();
      this.form.get('scheduleDescription').updateValueAndValidity();
    }
  }

  /**
   * set or reset values based on job type frequency value changed
   *
   * @param event - job frequency change event
   */
  setOrResetValue(event): void {
    if (!event?.detail?.value) {
      this.resetDaytime();
      this.form.get('jobOccurrence').reset();
      this.form.get('expectedStartDate').reset();
      this.form.get('expectedEndDate').reset();
      this.form.get('expectedStartTime').reset();
    } else if (event?.detail?.value && this.form.get('jobFrequency').value === 'ongoing') {
      if (!this.form.get('jobOccurrence').value) {
        this.form.get('jobOccurrence').setValue('weekly');
      }
      this.form.get('expectedStartDate').setValue(new Date().toISOString());
      this.form.get('expectedStartTime').reset();
    } else if (event?.detail?.value && this.form.get('jobFrequency').value === 'once_off') {
      this.form.get('expectedStartDate').setValue(new Date().toISOString());
      this.form.get('expectedStartTime').setValue(new Date().toISOString());
    }
    this.toggleValidationScheduleDescription();
  }

  /**
   * reset expected week day form group when jobOccurrence is "fortnightly" or worker not agreed on date and time.
   */
  private resetDaytime(): void {
    this.weekDaysOptions.forEach((expectedItem, index) => {
      this.form.controls.daysAndTimes.get(expectedItem).get('expected').setValue(false);
      const expectedSessionControl = this.form.controls.daysAndTimes
        .get(expectedItem)
        .get('expectedSessions') as UntypedFormArray;
      expectedSessionControl.removeAt(index);
    });
  }

  /**
   * Check mobile app rating prompt can show.
   */
  async canShowMobileRatingPrompt(): Promise<void> {
    try {
      if (await this.userFeedbackService.canShowMobileRatingPrompt()) {
        this.modalCtrl
          .create({
            component: UserExperienceFeedbackModalComponent,
          })
          .then((modal: HTMLIonModalElement) => modal.present());
      }
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }
}
